import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createDepartment,
  deleteDepartment,
  departmentsUser,
  updateDepartment
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";

const emptyForm = {
  departmentName: "",
  departmentCode: ""
};

const DepartmentManagement = () => {
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const filteredDepartments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return departments;

    return departments.filter((department) =>
      [department.departmentName, department.departmentCode]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [departments, search]);

  const loadDepartments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await departmentsUser();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading departments" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.departmentName.trim() || !formData.departmentCode.trim()) {
      dispatch(setAlert({ type: "danger", message: "Department name and code are required" }));
      return;
    }

    const payload = {
      departmentName: formData.departmentName.trim(),
      departmentCode: formData.departmentCode.trim().toUpperCase()
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateDepartment(editingId, payload);
        dispatch(setAlert({ type: "success", message: "Department updated successfully" }));
      } else {
        await createDepartment(payload);
        dispatch(setAlert({ type: "success", message: "Department created successfully" }));
      }

      resetForm();
      loadDepartments();
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to save department"
      }));
    } finally {
      setSaving(false);
    }
  };

  const editDepartment = (department) => {
    setEditingId(department._id);
    setFormData({
      departmentName: department.departmentName || "",
      departmentCode: department.departmentCode || ""
    });
  };

  const removeDepartment = async (department) => {
    if (!window.confirm(`Delete department "${department.departmentName}"?`)) return;

    try {
      await deleteDepartment(department._id);
      dispatch(setAlert({ type: "success", message: "Department deleted successfully" }));
      loadDepartments();
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Unable to delete department" }));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Departments</h3>
          <p>Create and maintain departments used in incident routing and reporting.</p>
        </div>
      </div>

      <div className="admin-layout">
        <form className="admin-form-panel" onSubmit={handleSubmit}>
          <div className="admin-panel-title">{editingId ? "Edit Department" : "Create Department"}</div>

          <label>
            <span>Department Name</span>
            <input
              className="form-control"
              value={formData.departmentName}
              onChange={(event) => setFormData((prev) => ({ ...prev, departmentName: event.target.value }))}
              placeholder="Emergency"
            />
          </label>

          <label>
            <span>Department Code</span>
            <input
              className="form-control"
              value={formData.departmentCode}
              onChange={(event) => setFormData((prev) => ({ ...prev, departmentCode: event.target.value }))}
              placeholder="ER"
            />
          </label>

          <div className="admin-form-actions">
            {editingId && (
              <button type="button" className="admin-secondary-btn" onClick={resetForm}>
                Cancel
              </button>
            )}
            <button type="submit" className="admin-primary-btn" disabled={saving}>
              <i className={`bi ${editingId ? "bi-check2" : "bi-plus-lg"}`} aria-hidden="true"></i>
              <span>{saving ? "Saving" : editingId ? "Update Department" : "Create Department"}</span>
            </button>
          </div>
        </form>

        <div className="dashboard-list-panel admin-grid-panel">
          <div className="dashboard-list-header">
            <div>
              <div className="admin-grid-title">Department List</div>
              <div className="list-count">{filteredDepartments.length} records</div>
            </div>
            <div className="dashboard-table-tools">
              <label>
                Search
                <input
                  className="admin-search-input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name or code"
                />
              </label>
              <button
                type="button"
                className="grid-icon-btn"
                onClick={loadDepartments}
                aria-label="Refresh departments"
                title="Refresh departments"
              >
                <i className="bi bi-arrow-repeat" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div className="modern-table-wrap">
            <table className="table modern-dashboard-table">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>Department Name</th>
                  <th>Department Code</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.length ? filteredDepartments.map((department, index) => (
                  <tr key={department._id}>
                    <td>{index + 1}</td>
                    <td className="admin-strong-cell">{department.departmentName}</td>
                    <td>
                      <span className="admin-code-pill">{department.departmentCode}</span>
                    </td>
                    <td>
                      <div className="admin-row-actions">
                        <button type="button" className="admin-icon-action" onClick={() => editDepartment(department)} title="Edit department">
                          <i className="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                        <button type="button" className="admin-icon-action danger" onClick={() => removeDepartment(department)} title="Delete department">
                          <i className="bi bi-trash" aria-hidden="true"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center text-muted">
                      {loading ? "Loading..." : "No departments found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentManagement;
